<script setup lang="ts">
/**
 * The frame every unauthenticated screen sits in: sign-in, the verification
 * gate, the verify-link result, the password reset and (through `slot`) the
 * forgot-password form. The screens a visitor meets before the game therefore
 * agree on their chrome by construction.
 *
 * A riveted steel card (`.k-panel`) under a navy enamel title plate
 * (`.k-header`), laid over the dusk anchorage painting, blurred and dimmed so
 * the card is the sharpest thing on screen. Same geometry as `GameWindow`,
 * minus its close button. The plate carries the anchor and the wordmark, or a
 * `title` and `icon` for a screen that names its state. Folder tabs go in the
 * `tabs` slot, quiet plates under the card in `after`.
 *
 * Owns: backdrop, card, plate, tab row. Must not: know any form's fields; the
 * fields live in `auth/AuthField.vue` and the screens that use them.
 */
import { computed } from 'vue';

import { useI18n } from '@/i18n';
import GameIcon from './kit/GameIcon.vue';
import type { GameIconName } from './kit/icons';

const props = withDefaults(
  defineProps<{ locked?: boolean; testId?: string; title?: string; icon?: GameIconName }>(),
  { locked: false, testId: undefined, title: undefined, icon: 'anchor' },
);

const { t } = useI18n();
const heading = computed(() => props.title ?? t('auth.brand'));
</script>

<template>
  <div class="auth-screen" :data-test="testId">
    <picture class="backdrop" aria-hidden="true">
      <source srcset="/art/loading-dusk.webp" type="image/webp" />
      <img src="/art/loading-dusk.jpg" alt="" decoding="async" />
    </picture>

    <div class="stack" :class="{ locked }">
      <header class="k-header head">
        <GameIcon class="head-icon" :name="icon" :size="56" />
        <h1 class="k-title head-title">{{ heading }}</h1>
      </header>

      <div v-if="$slots.tabs" class="tabs" role="tablist"><slot name="tabs" /></div>

      <section class="k-panel body">
        <slot />
      </section>

      <div v-if="$slots.after" class="after"><slot name="after" /></div>
    </div>
  </div>
</template>

<style scoped>
.auth-screen {
  position: relative;
  isolation: isolate;
  /*
   * Flex, not grid: a centred grid item shrink-wraps, which makes the stack's
   * own `width: min(…, 100%)` cyclic (on a 390 px phone the card came out
   * wider than the screen). A flex container's content box is definite.
   */
  display: flex;
  align-items: center;
  justify-content: center;
  /* The app's wrapper has no definite height, so a percentage would collapse; the viewport is the frame. */
  height: 100vh;
  height: 100dvh;
  overflow: auto;
  overflow-x: hidden;
  padding: clamp(0.75rem, 3vh, 2rem) 12px;
  padding-top: calc(clamp(0.75rem, 3vh, 2rem) + env(safe-area-inset-top, 0px));
  padding-bottom: calc(clamp(0.75rem, 3vh, 2rem) + env(safe-area-inset-bottom, 0px));
  padding-left: calc(12px + env(safe-area-inset-left, 0px));
  padding-right: calc(12px + env(safe-area-inset-right, 0px));
  background: #06090d;
}

/*
 * The painting, fixed to the screen so a tall card scrolls over it. Blurred and
 * scaled past the edge (a blur pulls the border in as transparent), dimmed by
 * brightness over the near-black under it.
 */
.backdrop {
  position: fixed;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
}

.backdrop img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
  transform: scale(1.06);
  filter: blur(4px) brightness(0.72) saturate(1.1);
}

.backdrop::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 45%, rgba(6, 9, 13, 0) 30%, rgba(6, 9, 13, 0.65) 100%);
}

.stack {
  position: relative;
  flex: 0 1 auto;
  width: min(26rem, 100%);
  min-width: 0;
  margin: auto 0;
  filter: drop-shadow(0 18px 30px rgba(0, 0, 0, 0.55));
  animation: rise 320ms var(--ease) both;
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(0.9rem);
  }
}

.stack.locked {
  opacity: 0.92;
}

/* The plate sits over the card's top edge, as in GameWindow. */
.head {
  position: relative;
  z-index: 2;
  margin: 0 8px -14px;
}

.head-icon {
  margin-left: -6px;
}

.head-title {
  margin: 0;
  font-size: clamp(1.4rem, 1rem + 1.4vw, 2rem);
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tabs {
  position: relative;
  z-index: 1;
  display: flex;
  gap: 6px;
  padding: 18px 20px 0;
  margin-bottom: -10px;
}

.tabs :deep(.btn) {
  flex: 1;
}

.body {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: clamp(26px, 3vw, 36px);
}

.after {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  padding-top: 12px;
}

/* A short landscape phone: the card widens so its form can run in two columns. */
@media (max-height: 520px) and (min-width: 640px) {
  .stack {
    width: min(44rem, 100%);
  }

  .body {
    padding-top: 22px;
    gap: 8px;
  }

  /* The plate and tabs shrink so the form gets the height; the tap targets stay 44 px. */
  .head {
    --h: 48px;
  }

  .head-icon {
    width: 40px;
    height: 40px;
  }

  .tabs {
    padding-top: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .stack {
    animation: none;
  }
}
</style>
